/**
 * @fileoverview Focus management hook for antd Dropdown + Menu keyboard accessibility.
 *
 * ## Problem
 *
 * antd's Dropdown component renders menus in a portal, but doesn't automatically
 * move focus to the menu when it opens. This breaks keyboard navigation because:
 *
 * 1. rc-menu (which powers antd Menu) has built-in keyboard navigation
 *    (ArrowUp/ArrowDown/Enter), but it only works when the menu container has focus.
 * 2. When a Dropdown opens (via click or Enter on trigger), focus stays on the
 *    trigger button - the menu never receives focus.
 * 3. Without focus on the menu, arrow keys do nothing.
 *
 * ## Solution
 *
 * This hook manages the focus handoff:
 * - When dropdown opens → focus the first enabled menu item (WAI-ARIA menu button pattern:
 *   "When a menu opens, keyboard focus is placed on the first item"). Focusing an rc-menu item
 *   fires its onFocus → onActive, so rc-menu's arrow-key nav continues from that item. Falls
 *   back to the menu container when there is no enabled item.
 * - When dropdown closes → return focus to the trigger button
 * - Handle Escape key → close dropdown (rc-menu's Escape is for submenus only)
 *
 * ## What rc-menu handles (once focused)
 *
 * - ArrowDown/ArrowUp: Navigate between menu items
 * - Enter: Activate the focused item
 * - Home/End: Jump to first/last item
 * - Type-ahead: Jump to items starting with typed character
 *
 * ## What this hook handles
 *
 * - Focus transfer: trigger → first menu item (on open)
 * - Focus transfer: menu → trigger (on close)
 * - Escape key: Close the dropdown entirely
 *
 * ## Timing: Why Double requestAnimationFrame?
 *
 * antd Dropdown renders its menu in a React portal asynchronously. The timing
 * between the visibility state change and the portal appearing in the DOM varies
 * depending on how the dropdown was triggered:
 *
 * - Mouse click: Portal renders quickly, single RAF often sufficient
 * - Keyboard Enter: Additional event handling delays portal rendering
 *
 * Timing options considered:
 *
 * | Method              | Timing           | Result                              |
 * |---------------------|------------------|-------------------------------------|
 * | queueMicrotask      | Before render    | Too early - portal doesn't exist    |
 * | Single RAF          | ~16ms            | Works for click, fails for keyboard |
 * | Double RAF          | ~32ms            | Works reliably for both triggers    |
 * | setTimeout(fn, 50)  | 50ms             | Works but arbitrary/slower          |
 *
 * Double RAF waits for:
 * 1. First RAF: React's commit phase completes (state → DOM mutation)
 * 2. Second RAF: Browser has painted, element is ready for focus
 *
 * ## antd Animation Note
 *
 * antd Dropdown uses a 200ms slide animation (@animation-duration-base in theme).
 * The DOM element exists at animation START, not end - so we can focus immediately.
 * The animation is purely visual and doesn't affect focusability.
 *
 * ## Usage
 *
 * This hook is used internally by BuiMenu when rendered inside a Dropdown.
 * See BuiMenu.stories.tsx "WithFocusManagement" for a complete example.
 */
import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { uniqueId } from "lodash-es";

/**
 * First enabled top-level item of `menu`. rc-menu gives enabled items and submenu titles
 * `tabindex="-1"` and disabled ones no tabindex; separators and group titles have no menuitem
 * role. Items of an inline nested submenu belong to that submenu's own `[role="menu"]`.
 */
function getFirstEnabledItem(menu: HTMLElement): HTMLElement | undefined {
  return Array.from(
    menu.querySelectorAll<HTMLElement>('[role="menuitem"][tabindex]'),
  ).find(
    (item) =>
      item.closest('[role="menu"]') === menu &&
      item.getAttribute("aria-disabled") !== "true",
  );
}

type DropdownOverlay = ReactElement | (() => ReactElement);

/**
 * Gives a dropdown's overlay an id so {@link useMenuFocusOnOpen} can find this dropdown's own menu.
 * Returns the id the menu will have - the overlay's own `id` if it already has one (e.g.
 * BTOverflow's menu), otherwise a generated one - and the overlay with that id applied. Function
 * overlays are wrapped so the element they return gets the generated id too (an id they set
 * themselves is kept, and the lookup then falls back to `menuSelector`).
 * @param overlay - The Dropdown's `overlay` prop.
 * @returns The menu's id and the overlay to pass to the Dropdown.
 */
export function useMenuOverlayId<T extends DropdownOverlay>(
  overlay: T,
): { menuId: string; overlay: T } {
  // uniqueId, not crypto.randomUUID: older mobile webviews lack it and render a blank page.
  const generatedId = useMemo(() => uniqueId("dropdown-menu-"), []);
  return useMemo(() => {
    const withId = (element: ReactElement) => {
      if (!isValidElement<{ id?: string }>(element)) {
        return { menuId: generatedId, element };
      }
      const ownId = element.props.id;
      return ownId
        ? { menuId: ownId, element }
        : {
            menuId: generatedId,
            element: cloneElement(element, { id: generatedId }),
          };
    };
    if (typeof overlay === "function") {
      return {
        menuId: generatedId,
        overlay: (() => withId(overlay()).element) as T,
      };
    }
    const { menuId, element } = withId(overlay);
    return { menuId, overlay: element as T };
  }, [overlay, generatedId]);
}

interface UseMenuFocusOnOpenOptions {
  /**
   * Ref to the trigger element (button that opens the dropdown).
   * Focus returns here when menu closes.
   */
  triggerRef: RefObject<HTMLElement | null>;
  /**
   * Whether the dropdown is currently visible.
   */
  visible: boolean;
  /**
   * Callback when visibility changes. Pass this to Dropdown's onVisibleChange.
   */
  onVisibleChange: (visible: boolean) => void;
  /**
   * Selector to find the menu container. Defaults to antd's menu classes. Used only when no
   * `menuId` is given, and matches the first such menu anywhere in the document.
   */
  menuSelector?: string;
  /**
   * Id of this dropdown's menu (the overlay element). When set, the menu is looked up by id, so
   * opening this dropdown can't focus another menu already in the document (a sidebar `.ant-menu`,
   * or a closed dropdown antd has hidden but kept mounted). BuiDropdown/BTDropdown set it.
   */
  menuId?: string;
  /**
   * When `false`, the hook does not move focus to the menu on open, return focus
   * to the trigger on close, or bind the Escape-to-close listener. Use for
   * combobox-style dropdowns whose trigger is a text input the user types into
   * (e.g. BTTimePicker) — stealing focus to the menu would drop keystrokes.
   * @default true
   */
  enabled?: boolean;
  /**
   * When `false`, focus is NOT returned to the trigger when the menu closes, while
   * focus-into-menu on open and Escape-to-close still work. Use when selecting a menu
   * item swaps in its own focus target (e.g. an inline input) that the trigger
   * focus-return would otherwise steal focus from.
   * @default true
   */
  returnFocusOnClose?: boolean;
}

interface UseMenuFocusOnOpenReturn {
  /**
   * Pass this to Dropdown's onVisibleChange prop.
   */
  handleVisibleChange: (visible: boolean) => void;
}

/**
 * Hook to manage focus handoff between dropdown trigger and menu.
 * When dropdown opens, focuses the menu's first enabled item (or the container if none) so rc-menu's built-in keyboard navigation starts there.
 * Pass `menuId` (the overlay's id) so only this dropdown's own menu is looked up.
 * When dropdown closes, returns focus to the trigger.
 * rc-menu handles ArrowUp/ArrowDown/Enter/Escape internally once it has focus.
 * @param options - Hook configuration options.
 * @param options.triggerRef - Ref to the trigger element. Focus returns here when menu closes.
 * @param options.visible - Whether the dropdown is currently visible.
 * @param options.onVisibleChange - Callback when visibility changes.
 * @param options.menuSelector - Selector to find the menu container when no `menuId` is given. Defaults to antd's menu classes.
 * @param options.menuId - Id of this dropdown's menu; scopes the lookup to it.
 * @param options.enabled - When `false`, disables all focus management (open focus, close focus-return, Escape-to-close). Defaults to `true`.
 * @example
 * ```tsx
 * const [visible, setVisible] = useState(false);
 * const triggerRef = useRef<HTMLButtonElement>(null);
 *
 * const menuId = useMemo(() => uniqueId("dropdown-menu-"), []);
 * const { handleVisibleChange } = useMenuFocusOnOpen({
 *   triggerRef,
 *   visible,
 *   onVisibleChange: setVisible,
 *   menuId,
 * });
 *
 * <Dropdown
 *   visible={visible}
 *   onVisibleChange={handleVisibleChange}
 *   overlay={<BuiMenu id={menuId}>...</BuiMenu>}
 * >
 *   <button ref={triggerRef}>Open</button>
 * </Dropdown>
 * ```
 */
export function useMenuFocusOnOpen({
  triggerRef,
  visible,
  onVisibleChange,
  menuSelector = ".ant-dropdown-menu, .ant-menu",
  menuId,
  enabled = true,
  returnFocusOnClose = true,
}: UseMenuFocusOnOpenOptions): UseMenuFocusOnOpenReturn {
  const previousVisibleRef = useRef(visible);
  const shouldReturnFocusRef = useRef(false);

  // Handle focus when visibility changes
  useEffect(() => {
    if (!enabled) return undefined;

    const wasVisible = previousVisibleRef.current;
    previousVisibleRef.current = visible;

    if (!wasVisible && visible) {
      // Dropdown just opened - focus the first item to activate rc-menu's keyboard nav
      // antd Dropdown renders portal asynchronously with a 200ms slide animation.
      // The DOM element exists at animation start, but we need to wait for React to
      // commit the portal. Double RAF waits ~32ms (2 paint cycles) which handles
      // timing differences between click and keyboard triggers.
      const focusMenu = () => {
        // The id is usually on the menu itself; if a custom overlay put it on a wrapper, use the
        // menu inside it.
        const byId = menuId ? document.getElementById(menuId) : null;
        const menu = byId
          ? byId.matches(menuSelector)
            ? byId
            : (byId.querySelector<HTMLElement>(menuSelector) ?? byId)
          : document.querySelector<HTMLElement>(menuSelector);
        if (menu) {
          (getFirstEnabledItem(menu) ?? menu).focus({ preventScroll: true });
          shouldReturnFocusRef.current = true;
        }
      };

      // Double RAF: first waits for React commit, second ensures DOM is ready.
      // Cancelled on cleanup so a fast open → close can't focus a menu that's already hidden.
      let innerId: number | undefined;
      const outerId = requestAnimationFrame(() => {
        innerId = requestAnimationFrame(focusMenu);
      });
      return () => {
        cancelAnimationFrame(outerId);
        if (innerId !== undefined) {
          cancelAnimationFrame(innerId);
        }
      };
    } else if (wasVisible && !visible && shouldReturnFocusRef.current) {
      if (returnFocusOnClose) {
        triggerRef.current?.focus();
      }
      shouldReturnFocusRef.current = false;
    }
    return undefined;
  }, [visible, triggerRef, menuSelector, enabled, returnFocusOnClose, menuId]);

  // Handle Escape key to close the dropdown
  // rc-menu's Escape handling is for submenus, not closing the Dropdown itself
  useEffect(() => {
    if (!enabled || !visible) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onVisibleChange(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled, visible, onVisibleChange]);

  const handleVisibleChange = useCallback(
    (newVisible: boolean) => {
      onVisibleChange(newVisible);
    },
    [onVisibleChange],
  );

  return { handleVisibleChange };
}

interface UseMenuOverlayOptions<T extends DropdownOverlay>
  extends Omit<UseMenuFocusOnOpenOptions, "menuId" | "menuSelector"> {
  /** The Dropdown's `overlay` prop. */
  overlay: T;
}

interface UseMenuOverlayReturn<T extends DropdownOverlay>
  extends UseMenuFocusOnOpenReturn {
  /** Pass this to the Dropdown's `overlay` prop - the overlay with this dropdown's menu id. */
  overlay: T;
  /** The id the menu has: its own `id` if it set one, otherwise a generated one. */
  menuId: string;
}

/**
 * Focus management for a dropdown's menu in one call: gives the overlay an id
 * ({@link useMenuOverlayId}) and wires {@link useMenuFocusOnOpen} to find the menu by it.
 * Use this from dropdown wrappers (BuiDropdown, BTDropdown) instead of the two hooks separately.
 * @param options - The overlay plus {@link useMenuFocusOnOpen}'s options.
 * @returns The overlay to render, its menu id, and the Dropdown `onVisibleChange` handler.
 * @example
 * ```tsx
 * const { overlay: menuOverlay, handleVisibleChange } = useMenuOverlay({
 *   overlay,
 *   triggerRef,
 *   visible,
 *   onVisibleChange: setVisible,
 * });
 *
 * <Dropdown visible={visible} onVisibleChange={handleVisibleChange} overlay={menuOverlay}>
 *   <button ref={triggerRef}>Open</button>
 * </Dropdown>
 * ```
 */
export function useMenuOverlay<T extends DropdownOverlay>({
  overlay,
  ...focusOptions
}: UseMenuOverlayOptions<T>): UseMenuOverlayReturn<T> {
  const { menuId, overlay: overlayWithId } = useMenuOverlayId(overlay);
  const { handleVisibleChange } = useMenuFocusOnOpen({
    ...focusOptions,
    menuId,
  });
  return { overlay: overlayWithId, menuId, handleVisibleChange };
}
